import React from "react";
import { Link } from "react-router-dom";
import preFormulation from "../../images/preFormulation.jpg";

export default function PreFormulServicesRedesign() {
  const categories = [
    {
      title: "Physicochemical Characterization",
      description: "Detailed evaluation of the physical and chemical behavior of the Drug Substance to establish robust drug design limits.",
      icon: (
        // Test tube / Flask Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      items: [
        "Physical properties and product characterization",
        "API Characterization",
        "Solubility profiling in buffers and non-aqueous solvents",
        "Hygroscopicity & micromeritic studies",
        "Morphology assessment",
        "Intrinsic dissolution profiling"
      ]
    },
    {
      title: "Compatibility & Stability Studies",
      description: "Rigorous interaction studies to ensure stability, safety, and compatibility across active ingredients, excipients, and packaging.",
      icon: (
        // Shield check / Stability Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      items: [
        "Drug Substance - excipient interaction studies",
        "Compatibility testing of APIs and excipients",
        "Formulation and packaging compatibility studies",
        "Solid-state and solution-state stability & photostability",
        "Comprehensive regulatory compliance compatibility"
      ]
    },
    {
      title: "Advanced R&D & Engineering",
      description: "Innovative screening, reverse engineering, and degradation pathway profiling to expedite early product lifecycle milestones.",
      icon: (
        // Molecule / DNA / Gear Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m0 14v1m-8-8h1m14 0h1" />
        </svg>
      ),
      items: [
        "API Accelerated degradation (pH, Heat, light, and atmosphere)",
        "Advanced reverse engineering and deformulation",
        "NCE screening for Product development",
        "Prototype formulation evaluations"
      ]
    }
  ];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* Hero Header */}
      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1579165466541-71702b859ec1?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Core R&amp;D Phase
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Preformulation Services
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            The vital backbone of robust formulation development. We evaluate critical physicochemical parameters to build "Quality in Design" from step one.
          </p>
        </div>
      </section>

      {/* Main Body Grid */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Image & Overview */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm relative overflow-hidden">
              <div className="relative h-64 md:h-80 w-full rounded-xl overflow-hidden mb-6 group border border-slate-100">
                <img 
                  src={preFormulation} 
                  alt="Laboratory Preformulation Research" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="text-white text-xs font-light">DelNova R&amp;D ViRACS Centre</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-3">Physicochemical R&amp;D Backbone</h3>
              <p className="text-slate-600 text-sm font-light leading-relaxed">
                Before formulating any drug product, understanding the drug substance is imperative. DelNova's preformulation team characterizes molecular kinetics, thermodynamic properties, and multi-state compatibilities to ensure stable and scalable dosage designs.
              </p>
            </div>

            {/* CTA Widget */}
            <div className="bg-gradient-to-br from-brand-blue to-brand-blue-light text-white rounded-2xl p-8 shadow-md flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-teal/10 rounded-full blur-xl group-hover:scale-110 transition-transform duration-500" />
              <div>
                <h4 className="text-lg font-bold mb-2">Need Custom Characterization?</h4>
                <p className="text-slate-300 text-sm font-light mb-6 leading-relaxed">
                  Connect with our R&amp;D team to request detailed solubility profiles, reverse engineering specs, or custom stability studies.
                </p>
              </div>
              <Link 
                to="/ContactUs"
                state={{ from: "Inquiry: Preformulation Services" }}
                className="w-fit inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-full shadow-sm text-brand-blue bg-white hover:bg-brand-teal hover:text-white transition-all duration-200"
              >
                Inquire About Preformulation
              </Link>
            </div>
          </div>

          {/* Right Column: Categorized Cards */}
          <div className="lg:col-span-7 space-y-8">
            {categories.map((cat, idx) => (
              <div 
                key={idx}
                className="bg-white border border-slate-100 hover:border-brand-teal/20 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300 shrink-0">
                    {cat.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-blue mb-2">{cat.title}</h3>
                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6">{cat.description}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {cat.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start text-slate-600 text-xs md:text-sm font-light">
                        <svg className="w-4 h-4 text-brand-teal mr-2 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
